/**
 * useHbarReleaseEvents — V2.J.1
 *
 * Reads the most recent HBAR treasury release events surfaced by the
 * /api/cron/check-hbar-release cron. The table has RLS SELECT for any
 * authenticated user, so the Hedera Monitor page can show a public
 * banner without exposing user-specific data.
 *
 * Behaviour :
 *  - Returns the N latest events (default 3) ordered by detected_at desc
 *  - Refreshes once on mount + every `refreshIntervalMs` (default 15 min)
 *  - Returns an empty list on RLS / network errors — the UI gracefully
 *    hides the banner rather than displaying an error.
 */

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase/client';

export interface HbarReleaseEvent {
  id: string;
  previous_released_hbar: number;
  new_released_hbar: number;
  delta_hbar: number;
  total_hbar: number;
  source_timestamp: string | null;
  detected_at: string;
  notified_user_count: number;
  note: string | null;
}

export interface UseHbarReleaseEventsOptions {
  limit?: number;
  refreshIntervalMs?: number | false;
}

export function useHbarReleaseEvents(options: UseHbarReleaseEventsOptions = {}) {
  const { limit = 3, refreshIntervalMs = 15 * 60 * 1000 } = options;
  const [events, setEvents] = useState<HbarReleaseEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchEvents = async () => {
      try {
        // Supabase generated types don't include the new
        // hbar_release_events table yet — cast via `any` until the
        // type regeneration step is run. RLS gate still enforced
        // server-side, so this is purely a typing concession.
        const { data, error: fetchError } = await (supabase as any)
          .from('hbar_release_events')
          // We intentionally exclude dry-run rows from the banner — they
          // shouldn't leak into the user-facing surface.
          .select(
            'id, previous_released_hbar, new_released_hbar, delta_hbar, total_hbar, source_timestamp, detected_at, notified_user_count, note',
          )
          .or('note.is.null,note.neq.dry-run')
          .order('detected_at', { ascending: false })
          .limit(limit);

        if (cancelled) return;

        if (fetchError) {
          // RLS-only or table-missing — degrade silently rather than
          // showing an error in the banner.
          setError(new Error(fetchError.message));
          setEvents([]);
        } else {
          // Supabase returns numerics as `string` — coerce here so the
          // UI can use them as numbers without per-row casting.
          const rows: HbarReleaseEvent[] = ((data || []) as Array<Record<string, unknown>>).map((row) => ({
            id: String(row.id),
            previous_released_hbar: Number(row.previous_released_hbar),
            new_released_hbar: Number(row.new_released_hbar),
            delta_hbar: Number(row.delta_hbar),
            total_hbar: Number(row.total_hbar),
            source_timestamp: (row.source_timestamp as string | null) ?? null,
            detected_at: String(row.detected_at),
            notified_user_count: Number(row.notified_user_count) || 0,
            note: (row.note as string | null) ?? null,
          }));
          setEvents(rows);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Unknown error'));
          setEvents([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchEvents();

    let intervalId: number | undefined;
    if (refreshIntervalMs) {
      intervalId = window.setInterval(fetchEvents, refreshIntervalMs);
    }
    return () => {
      cancelled = true;
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [limit, refreshIntervalMs]);

  return { events, isLoading, error, latest: events[0] ?? null };
}
